/**
 * Route guard factory: gates a route on a per-org feature flag
 * (`orgs/{orgId}.features`).
 *
 * ```ts
 * { path: 'nodes/review', canActivate: [orgFeatureGuard('nodeEditing')], … }
 * ```
 *
 * This guard is one rule for everyone, internal admins included, so acting
 * as an org shows exactly what that org's members see. A route staff must
 * reach in any org uses {@link orgFeatureGuardUnlessInternal} instead. A
 * route whose flag is off redirects to `/nodes`. Unauthenticated users are
 * sent to `/login`.
 */

import { CanActivateFn, GuardResult, Router } from '@angular/router';
import { effect, inject, Injector, runInInjectionContext } from '@angular/core';

import { AuthService } from '../firebase/auth.service';
import { CATCH_UP_MS, MembershipSessionService } from './membership-session.service';
import { OrgService } from './org.service';
import type { OrgFeatureName } from './orgs.types';

/**
 * Like {@link orgFeatureGuard}, but an internal admin passes without the flag
 * (`isInternalAdmin()` is already false while viewing as another user):
 * staff read a build set in any org, flag or not. Everyone else is judged
 * exactly as by `orgFeatureGuard`.
 */
export function orgFeatureGuardUnlessInternal(feature: OrgFeatureName): CanActivateFn {
  const members = orgFeatureGuard(feature);
  return async (route, state) => {
    const auth = inject(AuthService);
    const injector = inject(Injector);
    await waitForSignal(() => auth.claimsInitialized(), injector);
    if (auth.isInternalAdmin()) return true;
    return (await runInInjectionContext(injector, () => members(route, state))) as GuardResult;
  };
}

export function orgFeatureGuard(feature: OrgFeatureName): CanActivateFn {
  return async (route, state) => {
    const auth = inject(AuthService);
    const router = inject(Router);
    const org = inject(OrgService);
    const membership = inject(MembershipSessionService);
    const injector = inject(Injector);

    await waitForSignal(() => auth.claimsInitialized(), injector);

    if (!auth.isSignedIn()) {
      return router.createUrlTree(['/login'], {
        queryParams: state.url ? { returnUrl: state.url } : undefined,
      });
    }

    // A link may send this tab to another org (`?actAs=`). The shell applies
    // that only after NavigationEnd, which is after this guard, so apply it
    // here first: otherwise the route is judged by the org the tab was on.
    // `setTabOrg` ignores it for anyone but an internal admin, as the shell's
    // call does.
    const actAs = route.queryParamMap.get('actAs')?.trim();
    if (actAs) org.setTabOrg(actAs);

    // The org doc is readable only with the org's role in the token (or as
    // staff). A token that holds it never waits. One that is a refresh behind
    // (a fresh invite) read the doc as nothing: wait, bounded, for it to be
    // brought in line, and `OrgService` reads the doc again.
    const claims = auth.claims();
    const orgId = org.activeOrgId();
    const readable = claims.internalAdmin === true || (!!orgId && !!claims.orgRoles?.[orgId]);
    if (!readable) await waitForSignal(() => membership.settled(), injector, CATCH_UP_MS);

    // The flag lives on the org doc, which arrives a snapshot after the
    // active org id does — and until then the service still holds the
    // PREVIOUS org's doc. Wait for the doc of exactly the active org, so a
    // deep link is not bounced while it loads and one org is never judged by
    // another's flag. Never loading is treated as off.
    await waitForSignal(
      () => org.activeOrgId() !== null && org.activeOrgDocId() === org.activeOrgId(),
      injector,
      8_000,
    );

    if (org.hasFeature(feature)) return true;
    return router.createUrlTree(['/nodes']);
  };
}

/** Resolve once `predicate()` is true, or return false on timeout. */
function waitForSignal(
  predicate: () => boolean,
  injector: Injector,
  timeoutMs = 5_000,
): Promise<boolean> {
  if (predicate()) return Promise.resolve(true);
  return new Promise((resolve) => {
    let ref: { destroy(): void } | null = null;
    const timer = setTimeout(() => {
      ref?.destroy();
      resolve(false);
    }, timeoutMs);
    runInInjectionContext(injector, () => {
      ref = effect(() => {
        if (predicate()) {
          clearTimeout(timer);
          ref?.destroy();
          resolve(true);
        }
      });
    });
  });
}
